// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useRef, useState } from "react";
import HanziWriter from "hanzi-writer";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPencilAlt, faRotateRight, faVolumeHigh, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { speakJapanese, isSpeechSupported, SLOW_SPEECH_RATE } from "../../utils/speak";
import SlowTurtleIcon from "../SpeakButton/SlowTurtleIcon";
import styles from "./strokeOrderDiagram.module.scss";

const speechSupported = isSpeechSupported();
const CELEBRATE_DURATION_MS = 700;
const HANDAKUTEN_KATAKANA = new Set(["パ", "ピ", "プ", "ペ", "ポ"]);
const HANDAKUTEN_QUIZ_LENIENCY = 1.5;

function StrokeOrderDiagram({ symbol, autoQuiz, startQuizImmediately, previewAndDrawImmediately, onQuizComplete, onQuizStart, speakText, onPlaySound, perfectMessage }) {
    const { t } = useTranslation("common");
    const containerRef = useRef(null);
    const writerRef = useRef(null);
    const previewRef = useRef(null);
    const previewWriterRef = useRef(null);
    const replayPreviewRef = useRef(null);
    const celebrateTimeoutRef = useRef(null);
    const [quizMessage, setQuizMessage] = useState(null);
    const [quizActive, setQuizActive] = useState(false);
    const [celebrate, setCelebrate] = useState(false);
    const [completionCount, setCompletionCount] = useState(0);

    function getQuizOptions() {
        return {
            onComplete: handleQuizComplete,
            ...(HANDAKUTEN_KATAKANA.has(symbol)
                ? { leniency: HANDAKUTEN_QUIZ_LENIENCY, acceptBackwardsStrokes: true }
                : {}),
        };
    }

    function handleQuizComplete(summary) {
        setQuizMessage(summary.totalMistakes === 0 ? (perfectMessage || t("strokeDiagram.perfect")) : t("strokeDiagram.done"));
        setQuizActive(false);
        setCompletionCount((count) => count + 1);

        clearTimeout(celebrateTimeoutRef.current);
        setCelebrate(true);
        celebrateTimeoutRef.current = setTimeout(() => setCelebrate(false), CELEBRATE_DURATION_MS);

        onQuizComplete?.(summary);
    }

    useEffect(() => {
        /* istanbul ignore if -- containerRef is attached to the always-rendered canvas div before any effect runs; this only satisfies the null type */
        if (!containerRef.current) return undefined;
        containerRef.current.innerHTML = "";
        setQuizMessage(null);
        setQuizActive(false);
        setCelebrate(false);
        clearTimeout(celebrateTimeoutRef.current);

        const writerOptions = {
            width: 180,
            height: 180,
            padding: 10,
            strokeAnimationSpeed: 1,
            delayBetweenStrokes: 300,
            showOutline: true,
            strokeColor: "#1a1a2e",
            outlineColor: "#e2e8f0",
            drawingColor: "#e94560",
            drawingWidth: 40,
            charDataLoader: (character, onLoad, onError) => {
                // Keyed by codepoint, not by the character itself: 神 (U+795E) and its
                // jinmeiyo variant 神 (U+FA19) are distinct characters whose names
                // normalize to the same string on macOS, so filenames built from the
                // glyph collapse into one file in the working tree.
                const codepoint = character.codePointAt(0).toString(16);
                fetch(`${process.env.PUBLIC_URL}/stroke-data/${codepoint}.json`)
                    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`status ${res.status}`))))
                    .then(onLoad)
                    .catch(onError);
            },
        };
        const writer = HanziWriter.create(containerRef.current, symbol, writerOptions);
        writerRef.current = writer;

        if (autoQuiz || startQuizImmediately || previewAndDrawImmediately) {
            setQuizActive(true);
            onQuizStart?.();
            writer.quiz(getQuizOptions());
        } else {
            writer.animateCharacter();
        }

        return () => {
            writerRef.current = null;
            clearTimeout(celebrateTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbol, autoQuiz, startQuizImmediately, previewAndDrawImmediately]);

    useEffect(() => {
        if (!previewAndDrawImmediately || !previewRef.current) return undefined;
        previewRef.current.innerHTML = "";
        const previewWriter = HanziWriter.create(previewRef.current, symbol, {
            width: 76,
            height: 76,
            padding: 5,
            strokeAnimationSpeed: 1,
            delayBetweenStrokes: 300,
            showOutline: true,
            strokeColor: "#1a1a2e",
            outlineColor: "#e2e8f0",
            charDataLoader: (character, onLoad, onError) => {
                const codepoint = character.codePointAt(0).toString(16);
                fetch(`${process.env.PUBLIC_URL}/stroke-data/${codepoint}.json`)
                    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`status ${res.status}`))))
                    .then(onLoad)
                    .catch(onError);
            },
        });
        previewWriterRef.current = previewWriter;
        const replayPreview = () => {
            previewWriter.animateCharacter({
                onComplete: () => {
                    if (previewWriterRef.current === previewWriter) replayPreview();
                },
            });
        };
        replayPreviewRef.current = replayPreview;
        replayPreview();
        return () => { previewWriterRef.current = null; replayPreviewRef.current = null; };
    }, [symbol, previewAndDrawImmediately]);

    function startQuiz() {
        setQuizMessage(null);
        setQuizActive(true);
        setCelebrate(false);
        clearTimeout(celebrateTimeoutRef.current);
        onQuizStart?.();
        writerRef.current?.quiz(getQuizOptions());
    }

    return (
        <div className={styles.strokeDiagram}>
            {previewAndDrawImmediately && (
                <div className={styles.strokePreview} aria-label={t("strokeDiagram.previewLabel", { symbol })}>
                    <div ref={previewRef} className={styles.strokePreviewCanvas} aria-hidden="true" />
                    <button type="button" className={styles.strokePreviewReplay} onClick={() => {
                        previewWriterRef.current?.showCharacter();
                        replayPreviewRef.current?.();
                    }}>
                        <FontAwesomeIcon icon={faPlay} /> {t("strokeDiagram.replayPreview")}
                    </button>
                </div>
            )}
            <div
                ref={containerRef}
                className={[
                    styles.strokeCanvas,
                    !autoQuiz && !quizActive ? styles.strokeCanvasClickable : "",
                    quizActive ? styles.strokeCanvasDrawing : "",
                    celebrate ? styles.strokeCanvasCelebrate : "",
                ].filter(Boolean).join(" ")}
                onClick={!autoQuiz && !quizActive ? startQuiz : undefined}
            />
            {!autoQuiz && !previewAndDrawImmediately && (
                <div className={styles.strokeControls}>
                    <button
                        type="button"
                        className={styles.strokeButton}
                        onClick={() => {
                            setQuizMessage(null);
                            writerRef.current?.showCharacter();
                            writerRef.current?.animateCharacter();
                        }}
                    >
                        <FontAwesomeIcon icon={faPlay} /> {t("strokeDiagram.replay")}
                    </button>
                    <button type="button" className={styles.strokeButton} onClick={startQuiz}>
                        <FontAwesomeIcon icon={faPencilAlt} /> {t("strokeDiagram.practiceWriting")}
                    </button>
                    {speechSupported && speakText && (
                        <button
                            type="button"
                            className={styles.strokeButton}
                            onClick={() => {
                                speakJapanese(speakText);
                                onPlaySound?.();
                            }}
                        >
                            <FontAwesomeIcon icon={faVolumeHigh} /> {t("strokeDiagram.playSound")}
                        </button>
                    )}
                    {speechSupported && speakText && (
                        <button
                            type="button"
                            className={styles.strokeButton}
                            onClick={() => {
                                speakJapanese(speakText, { rate: SLOW_SPEECH_RATE });
                                onPlaySound?.();
                            }}
                        >
                            <SlowTurtleIcon /> {t("strokeDiagram.playSlowly")}
                        </button>
                    )}
                </div>
            )}
            {quizMessage && (
                <div className={styles.resultRow}>
                    <p className={styles.message} role="status">
                        <FontAwesomeIcon
                            key={completionCount}
                            icon={faCircleCheck}
                            className={styles.completeIcon}
                        />{" "}
                        {quizMessage}
                    </p>
                    {!autoQuiz && (
                        <button type="button" className={styles.strokeButton} onClick={startQuiz}>
                            <FontAwesomeIcon icon={faRotateRight} /> {t("strokeDiagram.tryAgain")}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default StrokeOrderDiagram;
